// Buy now — adds the product to cart, then jumps to checkout.
//
// Why this is non-trivial
// -----------------------
// WooCommerce's modern `add-to-cart-with-options` block is built on the
// WP Interactivity API. Unlike the older WC blocks, it does NOT emit the
// legacy `wc-blocks_added_to_cart` / `wc-blocks_add_to_cart_failed` DOM
// events on document.body. So listening for those events doesn't work —
// the cart updates silently, no completion signal.
//
// Approach
// --------
// 1. Watch the WC form's `.is-invalid` class. The form binds that class
//    to `!state.isFormValid` — so it covers both "no size picked yet"
//    AND "selected variation is out of stock." Mirror it onto the
//    Buy-now button (`data-form-invalid`) via MutationObserver so both
//    CTAs disable in lockstep.
//
// 2. On click of Buy now:
//      a) If form is invalid, click the WC button to surface the
//         validation message + bail.
//      b) Otherwise, hash the current cart (count + total) via the
//         public Store API.
//      c) Briefly wrap `window.fetch` to observe the upcoming WC
//         Store-API batch / add-item response without modifying it.
//      d) Click the WC button to fire the actual add-to-cart action.
//      e) Wait a beat, re-check `.is-invalid` — if it flipped on,
//         validation failed late; restore button.
//      f) Race two signals: the cart-hash poll (success) AND the
//         intercepted fetch response (failure with a WC error code,
//         e.g. partially-out-of-stock, invalid-nonce, etc.).
//      g) On success → redirect to /checkout/.
//         On error → surface the WC message on the button, then
//         restore after 4s so the user knows why nothing happened.
//         On timeout → restore.

const ADD_TIMEOUT_MS    = 6000;
const POST_CLICK_DELAY  = 250;  // wait for WC to set is-invalid on bad submit
const ERROR_VISIBLE_MS  = 4000; // how long an error label stays on the button
const CART_ENDPOINT     = '/wp-json/wc/store/v1/cart';
const ADD_API_PATTERN   = /\/wc\/store\/v1\/(batch|cart\/add-item)/;

const checkoutUrl = () => {
	const fromSettings = window?.wcSettings?.checkoutUrl
		|| window?.wc?.wcSettings?.CHECKOUT_URL
		|| null;
	return fromSettings || '/checkout/';
};

// Order matters: the notify-me form (.sentiments-notify-form) ALSO lives
// inside .wp-block-add-to-cart-with-options and has its own
// type="submit" button. A naive `button[type="submit"]` selector returns
// the notify-me submit first (it sits earlier in DOM order), causing
// Buy-now to click the wrong button — the WC add never fires and the
// busy state hangs on "Adding…". Start from the most specific WC class
// names and fall back to the form-scoped selectors that explicitly
// exclude the notify-me submit.
const findWcButton = (scope) => (
	scope.querySelector('button.single_add_to_cart_button')
	|| scope.querySelector('.wc-block-components-product-button button')
	|| scope.querySelector('.wp-block-add-to-cart-with-options button[type="submit"]:not(.sentiments-notify-form__submit)')
	|| scope.querySelector('.wc-block-add-to-cart-form button[type="submit"]:not(.sentiments-notify-form__submit)')
	|| null
);

const findWcForm = (scope) => (
	scope.querySelector('form.wp-block-add-to-cart-with-options')
	|| scope.querySelector('form.wc-block-add-to-cart-with-options')
	|| null
);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Fingerprint the current cart so we can detect when an item lands.
async function getCartHash() {
	try {
		const res = await fetch(CART_ENDPOINT, {
			credentials: 'same-origin',
			cache:       'no-store',
			headers:     { Accept: 'application/json' },
		});
		if (!res.ok) return null;
		const data = await res.json();
		const count = data?.items_count ?? 0;
		const total = data?.totals?.total_price ?? '';
		return `${count}:${total}`;
	} catch {
		return null;
	}
}

// Restore the button to its idle state.
function resetButton(btn, originalText) {
	btn.dataset.busy = '';
	btn.removeAttribute('aria-busy');
	btn.classList.remove('is-error');
	btn.textContent = originalText;
}

// Flash an error message on the button, then restore it.
function showButtonError(btn, originalText, message) {
	btn.dataset.busy = '';
	btn.removeAttribute('aria-busy');
	btn.classList.add('is-error');
	// WC messages can contain HTML entities (&quot; etc.) — decode by
	// roundtripping through a textarea, then truncate so the button
	// doesn't blow out the layout on long messages.
	const ta = document.createElement('textarea');
	ta.innerHTML = String(message || '').slice(0, 200);
	btn.textContent = ta.value.slice(0, 80);
	setTimeout(() => resetButton(btn, originalText), ERROR_VISIBLE_MS);
}

// Pull a human-friendly error message out of the WC batch response shape.
//   { responses: [{ status: 400, body: { code, message } }, ...] }
// Returns null when the response shows success.
function extractWcError(json) {
	if (!json) return null;
	const list = Array.isArray(json.responses) ? json.responses : [json];
	const bad  = list.find((r) => (r?.status >= 400) || r?.body?.code || r?.code);
	if (!bad) return null;
	return bad?.body?.message || bad?.message || 'Could not add to cart.';
}

// Briefly wrap window.fetch to observe (not modify) the next WC add-item
// request. Returns a promise that resolves with `{ error }` once the
// observed request finishes (or `{ error: null }` if no matching request
// fires before the parent times out). Always restores the original fetch.
function observeWcAddRequest(signal) {
	const original = window.fetch;
	return new Promise((resolve) => {
		let resolved = false;
		const finish = (result) => {
			if (resolved) return;
			resolved = true;
			window.fetch = original;
			resolve(result);
		};

		signal.addEventListener('abort', () => finish({ error: null }), { once: true });

		window.fetch = async function (...args) {
			const url = String(args[0]);
			const res = await original.apply(this, args);
			if (!ADD_API_PATTERN.test(url)) return res;
			try {
				const clone = res.clone();
				const json  = await clone.json();
				finish({ error: extractWcError(json) });
			} catch {
				finish({ error: res.ok ? null : `Add to cart failed (HTTP ${res.status}).` });
			}
			return res;
		};
	});
}

// Buy-now click flow.
async function buyNow(btn) {
	// Scope to the surrounding info column / product container so a
	// hypothetical multi-product page wouldn't cross-fire.
	const scope = btn.closest('.mbf-single-product') || document;
	const wcBtn  = findWcButton(scope);
	const wcForm = findWcForm(scope);

	// Simple product (no variation block) — fall back to classic
	// add-to-cart URL with redirect_to.
	if (!wcBtn) {
		const productId = btn.dataset.productId;
		const url = `/?add-to-cart=${productId}&redirect_to=${encodeURIComponent(checkoutUrl())}`;
		window.location.href = url;
		return;
	}

	// Already invalid (no size picked / OOS variation) — let WC show
	// its own validation message and bail.
	if (wcForm?.classList.contains('is-invalid')) {
		wcBtn.click();
		return;
	}

	const originalText = btn.textContent;
	btn.dataset.busy = 'true';
	btn.setAttribute('aria-busy', 'true');
	btn.classList.remove('is-error');
	btn.textContent  = 'Adding…';

	const initialHash = await getCartHash();

	// Start observing the upcoming WC add request BEFORE clicking.
	const abortObserver = new AbortController();
	const observerPromise = observeWcAddRequest(abortObserver.signal);

	// Fire the actual add-to-cart action.
	wcBtn.click();

	// Give WC a beat to flip is-invalid if late validation kicks in
	// without firing a network request (e.g. selected variant goes
	// out-of-stock client-side between mount and submit).
	await sleep(POST_CLICK_DELAY);
	if (wcForm?.classList.contains('is-invalid')) {
		abortObserver.abort();
		resetButton(btn, originalText);
		return;
	}

	// Race the observed add request against a hard timeout. The observer
	// settles as soon as WC's /wc/store/v1/batch (or /cart/add-item)
	// response lands — success or error — so we don't need to poll the
	// cart endpoint on top.
	const timeoutToken = Symbol('buy-now-timeout');
	const result = await Promise.race([
		observerPromise,
		sleep(ADD_TIMEOUT_MS - POST_CLICK_DELAY).then(() => timeoutToken),
	]);

	if (result === timeoutToken) {
		// The wrapped fetch never fired (or took absurdly long). Fall
		// back to a single cart-hash check before giving up — covers
		// the rare case where a plugin intercepts/replays the request.
		abortObserver.abort();
		const finalHash = await getCartHash();
		if (finalHash !== null && initialHash !== null && finalHash !== initialHash) {
			window.location.href = checkoutUrl();
			return;
		}
		resetButton(btn, originalText);
		return;
	}

	if (result?.error) {
		showButtonError(btn, originalText, result.error);
		return;
	}

	// Success path — add-to-cart returned 2xx with no inner error code.
	// The WC Store API mutates the server-side cart synchronously, so a
	// direct redirect is safe; no need to poll for cart propagation.
	window.location.href = checkoutUrl();
}

// Mirror the WC form's is-invalid state onto the Buy-now button so it
// can be styled / blocked from clicks in lockstep with the WC button.
function syncDisabledState(btn, scope) {
	const form = findWcForm(scope);
	if (!form) return;

	const sync = () => {
		if (form.classList.contains('is-invalid')) {
			btn.dataset.formInvalid = 'true';
			btn.setAttribute('aria-disabled', 'true');
		} else {
			delete btn.dataset.formInvalid;
			btn.removeAttribute('aria-disabled');
		}
	};

	new MutationObserver(sync).observe(form, {
		attributes: true,
		attributeFilter: ['class'],
	});

	sync(); // initial pass — the page often loads in invalid state
}

// ── Validation flow ─────────────────────────────────────────────────
// UX research feedback: the prior "fade buttons until variant is picked"
// state made customers think the site was broken. New flow:
//   - Both CTAs stay fully interactive at all times.
//   - Click on either CTA while form.is-invalid:
//       1. Show the "Please select a size" inline warning below the
//          variation selector.
//       2. Pulse the variation selector to draw the eye.
//       3. Smooth-scroll the variation selector into view (no-op if
//          already on screen).
//       4. Stop the click from doing anything else — no silent no-op.
//   - As soon as the user picks a size (form.is-invalid clears), the
//     warning auto-dismisses.
function setupValidationFlow(scope) {
	const form = findWcForm(scope);
	if (!form) return;

	const variationSelector = scope.querySelector(
		'.wp-block-woocommerce-add-to-cart-with-options-variation-selector'
	);
	if (!variationSelector) return; // simple product — no validation needed

	// Inject the warning slot ONCE, immediately after the variation
	// selector. Tagged with data-sentiments-validation so we don't add
	// duplicates if this init runs more than once.
	let warning = scope.querySelector('[data-sentiments-validation]');
	if (!warning) {
		warning = document.createElement('p');
		warning.className = 'sentiments-pdp-validation';
		warning.setAttribute('role', 'status');
		warning.setAttribute('aria-live', 'polite');
		warning.dataset.sentimentsValidation = '';
		warning.textContent = 'Please select a size before continuing.';
		variationSelector.insertAdjacentElement('afterend', warning);
	}

	let pulseTimer = 0;
	const showWarning = () => {
		warning.dataset.state = 'visible';

		// Re-fire the pulse animation by removing + re-adding the class.
		// `.is-pulsing` triggers a 720ms one-shot keyframe.
		variationSelector.classList.remove('is-pulsing');
		// Force reflow so the next add() restarts the animation.
		void variationSelector.offsetWidth;
		variationSelector.classList.add('is-pulsing');
		clearTimeout(pulseTimer);
		pulseTimer = setTimeout(() => variationSelector.classList.remove('is-pulsing'), 800);

		// Scroll into view if needed. `block: nearest` avoids unnecessary
		// scroll when the size selector is already on screen.
		variationSelector.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	};

	const hideWarning = () => {
		delete warning.dataset.state;
	};

	// Auto-clear: when the form becomes valid (user picked a size /
	// variation became OK), drop the warning.
	new MutationObserver(() => {
		if (!form.classList.contains('is-invalid')) hideWarning();
	}).observe(form, { attributes: true, attributeFilter: ['class'] });

	// Intercept ANY CTA click in the info column — capture phase so we
	// run before WC's own click handler. If form is invalid, prevent the
	// silent no-op and show the warning instead. Event delegation lets
	// this keep working across WC Interactivity re-renders.
	const CTA_SELECTOR = [
		'.wc-block-components-product-button__button',                            // WC Add-to-cart
		'form.wp-block-add-to-cart-with-options button[type="submit"]:not(.sentiments-notify-form__submit)', // any other WC submit
		'[data-buy-now]',                                                          // Buy Now
	].join(',');

	scope.addEventListener('click', (e) => {
		const cta = e.target.closest(CTA_SELECTOR);
		if (!cta) return;
		if (!form.classList.contains('is-invalid')) return;

		// Form is invalid — block the click and surface the warning.
		e.preventDefault();
		e.stopImmediatePropagation();
		showWarning();
	}, true);
}

export function initBuyNow() {
	const buttons = document.querySelectorAll('[data-buy-now]');

	// Validation flow runs whether the Buy-now button is present or not
	// (also covers the WC Add-to-cart on simple PDP loads). Hoist scope
	// detection so we don't duplicate the listener.
	const scope = (buttons[0]?.closest('.mbf-single-product'))
		|| document.querySelector('.mbf-single-product');
	if (scope) setupValidationFlow(scope);

	if (!buttons.length) return;

	buttons.forEach((btn) => {
		const btnScope = btn.closest('.mbf-single-product') || document;

		// Disabled-state mirroring (form `.is-invalid` → button data attr).
		// Kept as a semantic hook — buy-now's gating in the click handler
		// below also defends against accidental fire when form is invalid.
		syncDisabledState(btn, btnScope);

		// Click handler.
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			if (btn.dataset.busy === 'true') return;        // prevent double-click
			if (btn.dataset.formInvalid === 'true') return; // setupValidationFlow already showed the warning
			buyNow(btn);
		});
	});
}
